import classNames from "classnames";
import { Fragment, useEffect, useState } from "react";
import { Link } from "~/common/components/Link";
import Spinner from "~/common/components/Spinner/Spinner";
import { Table, TableBody, TableCell, TableHead, TableRow } from "~/common/components/Table/TableElements";
import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import Chip from "~/common/components/Chip/Chip";
import { TCell, TUpperCell, TUpperRow } from "~/features/orders/common/components/TableElements";
import { type QuoteListRow } from "~/features/orders/quoteList/useQuoteListState";
import { getIsAllOpen } from "~/features/orders/orderList/OrderListPage";
import { canQuoteListRowBeSelected } from "~/features/orders/quoteList/utils";
import {
  type HeaderCellData,
  useOrderListData,
} from "~/features/orders/orderList/components/OrderListTable/useOrderListData";
import { OrderDataSort } from "~/features/orders/orderList/components/OrderListTable/orderDataSorting";
import { isMultiAccountOrderSearchEnabled } from "~/utils/features";
import {
  QUOTE_DATA_SORT_HANDLERS,
  QuoteDataSort,
} from "~/features/orders/quoteList/components/QuoteListTable/quoteDataSorting";
import { getBodyCellCls } from "~/features/orders/orderList/components/OrderListTable/OrderListTable";
import { useTranslation } from "react-i18next";

import { isNonNull } from "~/common/utils/reducers";
import { QuoteDetailsTable } from "~/features/orders/common/components/v2/QuoteTable/QuoteDetailsTable";
import Checkbox from "~/common/components/Checkbox";
import type { QuoteListSelectionState } from "~/features/orders/common/components/v2/QuoteTable/useQuoteListSelectionState";
import { addStatusHover, isErrorEntry } from "~/features/orders/common/utils/util";
import { PopperTooltip } from "~/common/components/Tooltip";
import { DownloadContentButton } from "~/features/orders/quoteList/components/QuoteListTable/DownloadContentButton";
import { downloadFile } from "~/common/utils/ApiHelper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Alert } from "~/common/mui/Alert/Alert";

const QUOTE_HISTORY_HEADER_DATA: HeaderCellData[] = [
  {
    title: "quoteNumber",
    subtitle: "",
    sortKind: OrderDataSort.SO,
  },
  {
    title: "customerReferences",
    subtitle: "",
    sortKind: OrderDataSort.PO,
  },
  isMultiAccountOrderSearchEnabled() && {
    title: "account",
    subtitle: "endCustomer",
    sortKind: OrderDataSort.Account,
  },
  {
    title: "date",
    subtitle: "",
    sortKind: OrderDataSort.DatePlaced,
  },
  {
    title: "status",
    subtitle: "",
    sortKind: OrderDataSort.OrderStatus,
  },
  {
    title: "orderAttainment",
    subtitle: "",
    sortKind: QuoteDataSort.OrderAttainment,
  },
].filter(isNonNull);

interface QuoteListTableProps {
  rows: QuoteListRow[];
  className?: string;
  toggleOpen: (row: QuoteListRow) => void;
  toggleOpenAll: () => void;
  toggleSelectAllQuotes: () => void;
  toggleSelectQuote: (row: QuoteListRow) => void;
  toggleSelectLineItem: (soNumber: string, quoteLineNo: string) => void;
  selectionPerDocument: QuoteListSelectionState["selectionPerDocument"];
}

export const QuoteListTable = ({
  rows,
  toggleOpen,
  toggleOpenAll,

  selectionPerDocument,
  toggleSelectLineItem,
  toggleSelectQuote,
  toggleSelectAllQuotes,
}: QuoteListTableProps) => {
  const { t } = useTranslation();
  const { sortedRows, renderHeaderCell } = useOrderListData({
    rows,
    sortHandlers: QUOTE_DATA_SORT_HANDLERS,
    initialSortCode: OrderDataSort.DatePlaced,
    translationPrefix: "quoteListPage.table.header.",
  });

  const isRowFullySelected = (row: QuoteListRow) => {
    if (row.details) {
      const selectionStatus = selectionPerDocument[row.data.soNumber];
      return (selectionStatus?.isAllSelected || selectionStatus?.isAllDisabled) ?? false;
    } else {
      return !canQuoteListRowBeSelected(row);
    }
  };

  const isAllOpen = getIsAllOpen(rows);
  const isAllChecked = rows.every(isRowFullySelected);
  const isSomethingLoading = rows.some((row) => row.loadingDetails);
  const isSomethingSelectable = rows.length > 0 && rows.some(canQuoteListRowBeSelected);
  const isCheckAllDisabled = isSomethingLoading || !isSomethingSelectable;
  const [loading, setLoading] = useState({ id: "", state: false });

  const haveInvoices = sortedRows.filter((item) => item.data.orderStatusCode === "Q1")?.length > 0;

  const handleDownload = (row: QuoteListRow) => {
    if (!row.open) {
      toggleOpen(row);
    }
    setLoading({ id: row.data.quoteNumber, state: true });
  };

  const downloadQuoteDocument = async (invoiceUrl: string, id: string) => {
    if (invoiceUrl !== "" && id !== "") {
      await downloadFile(`${invoiceUrl}?id=${id}`);
    }
  };

  useEffect(() => {
    if (loading.state) {
      const row = sortedRows.find((row) => row.data.quoteNumber === loading.id);
      if (row?.detailsLoaded) {
        if (row.details?.soNumber) {
          const invoiceUrl = row.details?.quoteDocumentUrl || "";
          const quoteNumber = row.details?.soNumber;
          if (invoiceUrl !== "" && quoteNumber !== "") {
            void downloadQuoteDocument(invoiceUrl, quoteNumber).then(() => {
              setLoading({ id: row.data.quoteNumber, state: false });
            });
          }
        } else {
          setLoading({ id: loading.id, state: false });
        }
      }
    }
  }, [sortedRows, loading.id, loading.state]);

  const renderCheckbox = (row: QuoteListRow) => {
    const isSelected = selectionPerDocument[row.data.soNumber]?.isAllSelected ?? false;
    const canBeSelected = canQuoteListRowBeSelected(row);

    return (
      <Checkbox
        checked={isSelected}
        onCheck={() => toggleSelectQuote(row)}
        disabled={row.loadingDetails || !canBeSelected}
        data-test-id={"quoteLine-check-all"}
      />
    );
  };

  const showDownloadPDF = (row: QuoteListRow) => {
    if (isErrorEntry(row)) {
      return false;
    }
    if (row.detailsLoaded) {
      return row.data.orderStatusCode === "Q1" && row.data?.quoteDocumentUrl;
    } else {
      return row.data.orderStatusCode === "Q1";
    }
  };

  const renderDetails = (row: QuoteListRow) => {
    if (row.errorLoadingDetails) {
      return <Alert severity="error">{t("quoteListPage.error")}</Alert>;
    }
    if (!row.details) {
      return null;
    }
    const { quoteLines, soNumber } = row.details;
    const selectionStatus = selectionPerDocument[soNumber]!;
    return (
      <QuoteDetailsTable
        quoteLines={quoteLines}
        showCheckboxes={true}
        checkboxSx={{
          headerCheckbox: { marginRight: 23, alignSelf: "flex-end" },
          lineItemCheckbox: { marginRight: 23 },
        }}
        {...selectionStatus}
        toggleLine={(lineNo) => toggleSelectLineItem(soNumber, lineNo)}
      />
    );
  };

  return (
    <Table className={"scha__quotes__overview-table"}>
      <TableHead>
        <TableRow>
          {QUOTE_HISTORY_HEADER_DATA.map(renderHeaderCell)}
          <TableCell />
          {haveInvoices && <TableCell />}
          <TableCell
            padding="checkbox"
            sx={{
              display: "inline-flex !important",
              alignItems: "center",
              justifyContent: "flex-end",
              width: "100%",
              paddingRight: "6px",
              gap: "24px",
            }}
          >
            <Checkbox
              checked={isAllChecked}
              onCheck={toggleSelectAllQuotes}
              disabled={isCheckAllDisabled}
              data-test-id={"quotes-check-all"}
            />

            <Link
              className={"scha__quote__overview-table__close-all-button"}
              data-test-id={"close-all-button"}
              onClick={toggleOpenAll}
            >
              {isAllOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </Link>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedRows &&
          sortedRows.map((row, index) => (
            <Fragment key={index}>
              <TUpperRow
                key={index}
                className={classNames({
                  "scha__orders__overview-table__row": true,
                  open: row.open,
                })}
              >
                <TUpperCell className={getBodyCellCls(0)} data-test-id={`quoteNumber-${index}`}>
                  {row.data.soNumber}
                </TUpperCell>
                <TUpperCell className={getBodyCellCls(1)} data-test-id={`customerNumber-${index}`}>
                  {row.data.poNumber}
                </TUpperCell>
                {isMultiAccountOrderSearchEnabled() && (
                  <TUpperCell className={getBodyCellCls(2)} data-test-id={`account-${index}`}>
                    <div>
                      {row.data.account?.shortCode}, {row.data.account?.name}
                    </div>
                    <div className={"mt-1 f-label-color"}>
                      {row.data.account?.endCustomerCode}, {row.data.account?.endCustomerName}
                    </div>
                  </TUpperCell>
                )}
                <TUpperCell
                  className={getBodyCellCls(isMultiAccountOrderSearchEnabled() ? 3 : 2)}
                  data-test-id={`dateEntered-${index}`}
                >
                  {formatNullableDate(row.data.datePlaced, DateFormatType.DATE)}
                </TUpperCell>
                <TUpperCell
                  className={getBodyCellCls(isMultiAccountOrderSearchEnabled() ? 4 : 3)}
                  data-test-id={`quoteStatus-${index}`}
                >
                  <PopperTooltip
                    tooltipContent={<div style={{ width: 264 }}>{t("quote.status.pr.hover")}</div>}
                    hideTooltip={!addStatusHover(row.data.orderStatusCode)}
                    placement={"bottom"}
                  >
                    <Chip data-test-id={"quote-history-quote-status"}>{t(row.data.orderStatus)}</Chip>
                  </PopperTooltip>
                </TUpperCell>
                <TUpperCell className={getBodyCellCls(isMultiAccountOrderSearchEnabled() ? 5 : 4)}>
                  {row.data.orderAttainment}
                </TUpperCell>
                {haveInvoices && (
                  <TUpperCell className={getBodyCellCls(isMultiAccountOrderSearchEnabled() ? 6 : 5)}>
                    {showDownloadPDF(row) && (
                      <>
                        <DownloadContentButton
                          labelHover={t("quoteListPage.downloadDetails.helperLabel")}
                          data-test-id={"quote-list-download-pdf-button"}
                          key={`download-invoices-${row.data.poNumber}`}
                          label={t("downloadFile.type.pdf")}
                          onClick={() => handleDownload(row)}
                          isLoading={loading.id === row.data.quoteNumber && loading.state === true}
                        />
                      </>
                    )}
                  </TUpperCell>
                )}
                <TUpperCell>
                  {!isErrorEntry(row) && (
                    <Link data-test-id={"quote-history-quote-details-button"} href={row.data.detailsUrl}>
                      {t("quoteDetailsPage.title")}
                    </Link>
                  )}
                </TUpperCell>
                <TUpperCell>
                  {!isErrorEntry(row) && (
                    <div className={"scha__orders__overview-table__open-close-button"}>
                      {renderCheckbox(row)}
                      <Link onClick={() => toggleOpen(row)}>
                        {row.open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </Link>
                    </div>
                  )}
                </TUpperCell>
              </TUpperRow>
              {row.open && row.loadingDetails && (
                <TableRow
                  className={classNames({
                    "scha__orders__overview-table__row": true,
                    "scha__orders__overview-table__spacer": true,
                    open: true,
                  })}
                >
                  <TCell colSpan={12}>
                    <Spinner />
                  </TCell>
                </TableRow>
              )}
              {row.open && !row.loadingDetails && (
                <>
                  <TableRow
                    key={index + "d"}
                    className={classNames({
                      "scha__orders__overview-table__row": true,
                      "scha__orders__overview-table__details-row": true,
                      open: true,
                    })}
                  >
                    <TCell
                      className={"scha__orders__overview-table__details-cell"}
                      colSpan={isMultiAccountOrderSearchEnabled() ? (haveInvoices ? 9 : 8) : haveInvoices ? 8 : 7}
                    >
                      {renderDetails(row)}
                    </TCell>
                  </TableRow>
                  <TableRow
                    className={classNames({
                      "scha__orders__overview-table__row": false,
                      "scha__orders__overview-table__spacer": true,
                      open: true,
                    })}
                  >
                    <TCell colSpan={12} />
                  </TableRow>
                </>
              )}
            </Fragment>
          ))}
      </TableBody>
    </Table>
  );
};
